import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LocalPaginator from '@/features/shared/ui/LocalPaginator';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

/**
 * ExternalCoResearchers
 * - Presentational component for External Co-Researchers list with local pagination
 * - Props: { active, data, loading, perPage=6, error, linkMode }
 */
export default function ExternalCoResearchers({ active = false, data = [], loading = false, perPage = 6, error = '', linkMode = 'path' }) {
  const rows = Array.isArray(data) ? data : [];

  // pagination state
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * perPage;
  const slice = useMemo(() => rows.slice(start, start + perPage), [rows, start, perPage]);

  useEffect(() => {
    // reset page when data length changes to avoid empty slice
    setPage(1);
  }, [total]);

  const orgText = (row) => row?.org_name || row?.authority_org_name || '';
  const num = (v) => { const n = Number(v || 0) || 0; try { return n.toLocaleString(); } catch { return String(n); } };

  const hasRows = Array.isArray(slice) && slice.length > 0;

  return (
    <div>
      <div className="relative min-h-[220px]">
        {(loading || !active) && (
          <ChartSkeletonLoader show={true} />
        )}

        {!loading && hasRows && (
          <ul className="flex flex-col gap-2 md:flex-row md:flex-wrap lg:gap-3">
            {slice.map((row, idx) => {
              const id = row?.scholar_id;
              const canNavigate = id != null && String(id).trim() !== '' && !isNaN(Number(id));
              const to = linkMode === 'query'
                ? `/researchers/?id=${encodeURIComponent(id)}`
                : `/researchers/${encodeURIComponent(id)}`;
              const onCardClick = (e) => {
                try { if (e) e.preventDefault(); } catch {}
                if (!canNavigate) return;
                navigate(to);
                try {
                  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                    setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' }), 0);
                  }
                } catch {}
              };
              const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') onCardClick(e); };
              return (
              <li
                key={row?.scholar_id ? `${row.scholar_id}-${idx}` : idx}
                className={`px-6 py-[22px] leading-[140%] border border-[#E4E8F1] rounded-[12px] md:w-[calc((100%-8px)/2)] lg:w-[calc((100%-12px)/2)] hover:shadow-[0px_4px_20px_rgba(0,0,0,0.1)] transition-shadow duration-300 ${canNavigate ? 'cursor-pointer' : ''}`}
                role={canNavigate ? 'button' : undefined}
                tabIndex={canNavigate ? 0 : -1}
                aria-label={canNavigate ? `${row?.author_name || '—'} 프로필로 이동` : undefined}
                onClick={onCardClick}
                onKeyDown={onKey}
              >
                <a href="#" className="flex items-center justify-between gap-[10px]" onClick={(e) => e.preventDefault()}>
                  <div>
                    <p className="text-[14px] font-bold">{row?.author_name || '—'}</p>
                    <p className="mt-[5px] text-[13px] text-[#6C7687]">{orgText(row)}</p>
                  </div>
                  <div className="shrink-0 w-[68px] text-center">
                    <p className="text-[12px] text-[#6C7687]">논문 수</p>
                    <p className="text-[20px] font-bold">{num(row?.count)}</p>
                  </div>
                </a>
              </li>
              );
            })}
          </ul>
        )}

        {!loading && !hasRows && active && (
          <div className="noresult flex justify-center items-center !h-[160px] !cursor-default text-[13px] font-semibold border border-[#E4E8F1] rounded-[12px]">
            <div>{error ? error : 'External Co-Researchers 데이터가 없습니다.'}</div>
          </div>
        )}
      </div>

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 font-[Pretendard] text-[15px] mt-[50px] lg:mt-[40px]">
          <LocalPaginator total={total} perPage={perPage} page={safePage} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
